import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Check, Download, Printer, Camera,X, AlertTriangle, Info, Edit, Trash2, } from 'lucide-react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import logo from './assets/logo.png';
import { buildApiUrl, API_ENDPOINTS } from './utils/baseurl';


// Step Progress Component
const StepProgress = ({ currentStep, steps }) => {
  return (
    <div className="w-full mb-8 px-4">
      <div className="relative flex items-center justify-between max-w-full mx-auto">
        {/* Background line */}
        <div className="absolute top-5 left-8 right-8 h-0.5 bg-gray-300 z-0"></div>
        
        {/* Progress line */}
        <div 
          className="absolute top-5 left-8 h-0.5 bg-orange-500 transition-all duration-300 z-0"
          style={{ 
            width: currentStep === 0 ? '0%' : `calc(${(currentStep / (steps.length - 1)) * 100}% - ${16 / steps.length}px)`
          }}
        ></div>
        
        {steps.map((step, index) => (
          <div key={index} className="flex flex-col items-center relative z-10 min-w-0">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all duration-300 ${
              index < currentStep 
                ? 'bg-orange-500 border-orange-500 text-white' 
                : index === currentStep 
                ? 'bg-white border-orange-500 text-orange-500' 
                : 'bg-gray-200 border-gray-300 text-gray-500'
            }`}>
              {index < currentStep ? <Check size={16} /> : index + 1}
            </div>
            <span className={`text-xs mt-2 font-medium text-center max-w-20 leading-tight ${
              index === currentStep ? 'text-orange-500' : 'text-gray-500'
            }`}>
              {step}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};
  

// Personal Details Form
const PersonalDetailsForm = ({ formData, setFormData, onNext, setGeneratedId, setShowModal, setModalConfig, editLoan, loanId, generatedId,hideTitle }) => {
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Memoize the fetch function to prevent unnecessary re-renders
  const fetchLoanData = useCallback(async () => {
    if (!editLoan || !loanId) return;
    
    setIsLoading(true);
    try {
      const accessToken = localStorage.getItem('accessToken');

      if (!accessToken) {
        alert('Access token not found. Please login again.');
        return;
      }

      const response = await fetch(buildApiUrl(API_ENDPOINTS.LOAN_GET_BY_ID(loanId)), {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Only update form data if it's different from current data
        const newFormData = {
          loanHolderName: data.loanHolderName || '',
          mobileNumber: data.mobileNumber || '',
          address: data.address || '',
          city: data.city || '',
          district: data.district || '',
          state: data.state || '',
          pincode: data.pincode || '',
          status: data.status || 'A'
        };
        
        // Only update if the data has actually changed
        setFormData(prevData => {
          if (JSON.stringify(prevData) !== JSON.stringify(newFormData)) {
            return newFormData;
          }
          return prevData;
        });
      } else {
        console.error('Failed to fetch loan data');
        setModalConfig({
          title: 'Error',
          message: 'Failed to load loan data. Please try again.',
          type: 'error'
        });
        setShowModal(true);
      }
    } catch (error) {
      console.error('Error fetching loan data:', error);
      setModalConfig({
        title: 'Error',
        message: 'Network error while loading loan data.',
        type: 'error'
      });
      setShowModal(true);
    } finally {
      setIsLoading(false);
    }
  }, [editLoan, loanId, setFormData, setModalConfig, setShowModal]);

  // Optimize useEffect with proper dependencies and early return
  useEffect(() => {
    // Only fetch if we're in edit mode and have a loanId
    if (editLoan && loanId && !formData.loanHolderName) {
      fetchLoanData();
    }
  }, [editLoan, loanId, fetchLoanData, formData.loanHolderName]);

  // Memoize validation function
  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!formData.loanHolderName?.trim()) {
      newErrors.loanHolderName = "Loan Holder Name is required";
    }

    if (!formData.mobileNumber?.trim()) {
      newErrors.mobileNumber = "Mobile Number is required";
    } else if (!/^\d{10}$/.test(formData.mobileNumber)) {
      newErrors.mobileNumber = "Mobile Number must be exactly 10 digits";
    }

    if (!formData.address?.trim()) {
      newErrors.address = "Address is required";
    }

    if (!formData.city?.trim()) {
      newErrors.city = "City is required";
    }

    if (!formData.district?.trim()) {
      newErrors.district = "District is required";
    }

    if (!formData.state?.trim()) {
      newErrors.state = "State is required";
    }

    if (!formData.pincode?.trim()) {
      newErrors.pincode = "Pin Code is required";
    } else if (!/^\d{6}$/.test(formData.pincode)) {
      newErrors.pincode = "Pin Code must be exactly 6 digits";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  // Optimize form submission
  const handleSubmit = useCallback(async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const accessToken = localStorage.getItem('accessToken');
      
      if (!accessToken) {
        alert('Access token not found. Please login again.');
        return;
      }

      // Determine the correct ID to use
      let idToUse = 0;
      if (editLoan && loanId) {
        idToUse = loanId;
      } else if (generatedId) {
        idToUse = generatedId;
      }

      const payload = {
        id: idToUse,
        clientID: 0,
        loanNumber: "string",
        status: formData.status || "A", 
        loanHolderName: formData.loanHolderName,
        mobileNumber: formData.mobileNumber,
        address: formData.address,
        city: formData.city,
        district: formData.district,
        state: formData.state,
        pincode: formData.pincode,
        lastActionDate: new Date().toISOString(),
        lastActionUserID: 0
      };

      const response = await fetch(buildApiUrl(API_ENDPOINTS.LOAN_SAVE), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const result = await response.json();
        const resultId = result.id || result;
        
        // Only set generatedId if we don't already have one (for new loans)
        if (!generatedId && !editLoan) {
          setGeneratedId(resultId);
        }
        
        setModalConfig({
          title: 'Success',
          message: (editLoan || generatedId) ? 'Personal info updated successfully!' : 'Personal info saved successfully!',
          type: 'success'
        });
        setShowModal(true);
        
        // Move to next step
        onNext();
      } else {
        const errorData = await response.json();
        console.error('API Error:', errorData);
        setModalConfig({
          title: 'Error',
          message: (editLoan || generatedId) ? 'Failed to update personal info. Please try again.' : 'Failed to save personal info. Please try again.',
          type: 'error'
        });
        setShowModal(true);
      }
    } catch (error) {
      console.error('Network Error:', error);
      setModalConfig({
        title: 'Error',
        message: 'Network error. Please check your connection and try again.',
        type: 'error'
      });
      setShowModal(true);
    } finally {
      setIsSubmitting(false);
    }
  }, [validateForm, formData, editLoan, loanId, generatedId, setGeneratedId, setModalConfig, setShowModal, onNext]);

  // Optimize input change handler
  const handleInputChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  }, [setFormData, errors]);

  const handleStatusChange = useCallback((value) => {
    setFormData(prev => ({ ...prev, status: value }));
  }, [setFormData]);

  // Early return for loading state
  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mr-3"></div>
        <div className="text-lg">Loading loan data...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {!hideTitle && <h2 className="text-2xl font-bold text-gray-900 mb-6">
        {editLoan ? 'Edit Personal Info' : 'Personal Info'}
      </h2>}
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Loan Holder Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.loanHolderName || ''}
            onChange={(e) => handleInputChange('loanHolderName', e.target.value)}
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none ${
              errors.loanHolderName ? 'border-red-500' : 'border-gray-300'
            }`}
            required
          />
          {errors.loanHolderName && (
            <p className="text-red-500 text-sm mt-1">{errors.loanHolderName}</p>
          )}
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Mobile Number <span className="text-red-500">*</span>
          </label>
          <input
            type="tel"
            value={formData.mobileNumber || ''}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '');
              if (value.length <= 10) {
                handleInputChange('mobileNumber', value);
              }
            }}
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none ${
              errors.mobileNumber ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="10 digits"
            maxLength="10"
            required
          />
          {errors.mobileNumber && (
            <p className="text-red-500 text-sm mt-1">{errors.mobileNumber}</p>
          )}
        </div>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Address <span className="text-red-500">*</span>
        </label>
        <textarea
          rows={3}
          value={formData.address || ''}
          onChange={(e) => handleInputChange('address', e.target.value)}
          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none ${
            errors.address ? 'border-red-500' : 'border-gray-300'
          }`}
          required
        />
        {errors.address && (
          <p className="text-red-500 text-sm mt-1">{errors.address}</p>
        )}
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            City <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.city || ''}
            onChange={(e) => handleInputChange('city', e.target.value)}
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none ${
              errors.city ? 'border-red-500' : 'border-gray-300'
            }`}
            required
          />
          {errors.city && (
            <p className="text-red-500 text-sm mt-1">{errors.city}</p>
          )}
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            District <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.district || ''}
            onChange={(e) => handleInputChange('district', e.target.value)}
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none ${
              errors.district ? 'border-red-500' : 'border-gray-300'
            }`}
            required
          />
          {errors.district && (
            <p className="text-red-500 text-sm mt-1">{errors.district}</p>
          )}
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            State <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.state || ''}
            onChange={(e) => handleInputChange('state', e.target.value)}
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none ${
              errors.state ? 'border-red-500' : 'border-gray-300'
            }`}
            required
          />
          {errors.state && (
            <p className="text-red-500 text-sm mt-1">{errors.state}</p>
          )}
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Pin Code <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.pincode || ''}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '');
              if (value.length <= 6) {
                handleInputChange('pincode', value);
              }
            }}
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none ${
              errors.pincode ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="6 digits"
            maxLength="6"
            required
          />
          {errors.pincode && (
            <p className="text-red-500 text-sm mt-1">{errors.pincode}</p>
          )}
        </div>
      </div>

      {/* Status Radio Buttons */}
     <div className={editLoan ? '' : 'hidden'}>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Status <span className="text-red-500">*</span>
        </label>
        <div className="flex space-x-6">
          <label className="flex items-center">
            <input
              type="radio"
              name="status"
              value="A"
              checked={formData.status === 'A' || !formData.status}
              onChange={(e) => handleStatusChange(e.target.value)}
              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
            />
            <span className="ml-2 text-sm text-gray-700">Active</span>
          </label>
          <label className="flex items-center">
            <input
              type="radio"
              name="status"
              value="C"
              checked={formData.status === 'C'}
              onChange={(e) => handleStatusChange(e.target.value)}
              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
            />
            <span className="ml-2 text-sm text-gray-700">Closed</span>
          </label>
        </div>
      </div>

      <div className="flex justify-end space-x-4 mt-6">
        <button
          type="button"
          onClick={() => window.history.back()}
          className="px-6 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="px-6 py-3 bg-indigo-900 text-white rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isSubmitting ? (editLoan || generatedId ? 'Updating...' : 'Saving...') : (editLoan || generatedId ? 'Update' : 'Save & Next')}
        </button>
      </div>
    </div>
  );
};

// Custom Modal Component
const CustomSnackbar = ({
  isOpen,
  onClose,
  title,
  message,
  type = 'success',
  autoHideDuration = 2000 // 2 seconds by default
}) => {
  useEffect(() => {
    if (isOpen && autoHideDuration > 0) {
      const timer = setTimeout(() => {
        onClose();
      }, autoHideDuration);

      return () => clearTimeout(timer);
    }
  }, [isOpen, autoHideDuration, onClose]);

  if (!isOpen) return null;

  const getIconAndColor = () => {
    switch (type) {
      case 'success':
        return {
          bgColor: 'bg-green-50 border-green-200',
          textColor: 'text-green-600',
          icon: <Check size={20} />
        };
      case 'error':
        return {
          bgColor: 'bg-red-50 border-red-200',
          textColor: 'text-red-600',
          icon: <X size={20} />
        };
      case 'warning':
        return {
          bgColor: 'bg-yellow-50 border-yellow-200',
          textColor: 'text-yellow-600',
          icon: <AlertTriangle size={20} />
        };
      default:
        return {
          bgColor: 'bg-blue-50 border-blue-200',
          textColor: 'text-blue-600',
          icon: <Info size={20} />
        };
    }
  };

  const { bgColor, textColor, icon } = getIconAndColor();

  return (
    <div className="fixed top-4 right-4 z-50 animate-in slide-in-from-top-2 duration-300">
      <div className={`${bgColor} border rounded-lg shadow-lg p-4 max-w-sm w-full`}>
        <div className="flex items-start justify-between">
          <div className="flex items-start">
            <div className={`flex-shrink-0 ${textColor} mr-3 mt-0.5`}>
              {icon}
            </div>
            <div className="flex-1 min-w-0">
              {title && (
                <h4 className="text-sm font-medium text-gray-900 mb-1">
                  {title}
                </h4>
              )}
              <p className="text-sm text-gray-700">
                {message}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="flex-shrink-0 ml-3 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};


// Gold Details Form
const GoldDetailsForm = ({ formData, setFormData, generatedId, setShowModal, setModalConfig, editLoan, loanId, hideTitle, onValidationChange, onNext, onPrev, hideNavigation }) => {
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [goldInfoList, setGoldInfoList] = useState([]);
  const [editingGoldId, setEditingGoldId] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deletingGoldId, setDeletingGoldId] = useState(null);
  const [validationError, setValidationError] = useState('');
  const [isValidForNext, setIsValidForNext] = useState(false);

  // Calculate total values automatically
  const calculateTotalValues = useCallback(() => {
    const netWeight = parseFloat(formData.netWeight) || 0;
    const grossWeight = parseFloat(formData.grossWeight) || 0;
    const goldValuePerGram = parseFloat(formData.goldValuePerGram) || 0;
    
    return {
      totalGoldValueGross: grossWeight * goldValuePerGram,
      totalGoldValueNet: netWeight * goldValuePerGram
    };
  }, [formData.netWeight, formData.grossWeight, formData.goldValuePerGram]);

  // Update total values when weights or gold value per gram changes
  useEffect(() => {
    const { totalGoldValueGross, totalGoldValueNet } = calculateTotalValues();
    if (totalGoldValueGross > 0 || totalGoldValueNet > 0) {
      setFormData(prev => ({ 
        ...prev, 
        totalGoldValueGross: totalGoldValueGross,
        totalGoldValueNet: totalGoldValueNet
      }));
    }
  }, [calculateTotalValues, setFormData]);

  // Validation function for checking if at least one gold detail exists
  const validateGoldDetails = useCallback(() => {
    const hasGoldDetails = goldInfoList.length > 0;
    const errorMessage = hasGoldDetails ? '' : 'At least one gold detail must be added before proceeding to the next step.';
    
    setValidationError(errorMessage);
    setIsValidForNext(hasGoldDetails);
    
    // Call parent validation callback if provided
    if (onValidationChange) {
      onValidationChange(hasGoldDetails, errorMessage);
    }
    
    return hasGoldDetails;
  }, [goldInfoList.length, onValidationChange]);

  // Update validation whenever goldInfoList changes
  useEffect(() => {
    validateGoldDetails();
  }, [validateGoldDetails]);

  const fetchGoldInfo = useCallback(async () => {
    try {
      const accessToken = localStorage.getItem('accessToken');
      if (!accessToken) return;

      // Use loanId if in edit mode, otherwise use generatedId
      const currentLoanId = editLoan ? loanId : generatedId;
      
      if (!currentLoanId) {
        return;
      }

      const response = await fetch(buildApiUrl(API_ENDPOINTS.GOLD_GET_BY_LOAN_ID(currentLoanId)), {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setGoldInfoList(data);
      } else {
        // Failed to fetch gold info
      }
    } catch (error) {
      // Error fetching gold info
    }
  }, [generatedId, loanId, editLoan]);

  // Fetch gold info on component mount or when relevant IDs change
  useEffect(() => {
    const currentLoanId = editLoan ? loanId : generatedId;
    if (currentLoanId) {
      fetchGoldInfo();
    }
  }, [generatedId, loanId, editLoan, fetchGoldInfo]);

  // Handle Next button click with validation
  const handleNext = () => {
    if (!validateGoldDetails()) {
      // Show validation error in modal
      setModalConfig({
        title: 'Validation Error',
        message: 'Please add at least one gold detail before proceeding to the next step.',
        type: 'error'
      });
      setShowModal(true);
      return;
    }
    
    // If validation passes, proceed to next step
    if (onNext) {
      onNext();
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.goldType?.trim()) {
      newErrors.goldType = "Gold Type is required";
    }

    if (!formData.quantity || formData.quantity <= 0) {
      newErrors.quantity = "Quantity must be greater than 0";
    }

    if (!formData.grossWeight || parseFloat(formData.grossWeight) <= 0) {
      newErrors.grossWeight = "Gross Weight must be greater than 0";
    }

    if (!formData.netWeight || parseFloat(formData.netWeight) <= 0) {
      newErrors.netWeight = "Net Weight must be greater than 0";
    }

    if (!formData.goldValuePerGram || parseFloat(formData.goldValuePerGram) <= 0) {
      newErrors.goldValuePerGram = "Gold Value per Gram must be greater than 0";
    }

    // Check if net weight is less than or equal to gross weight
    if (formData.netWeight && formData.grossWeight && 
        parseFloat(formData.netWeight) > parseFloat(formData.grossWeight)) {
      newErrors.netWeight = "Net Weight cannot be greater than Gross Weight";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleEdit = (goldItem) => {
    // Set form data with the selected gold item data
    setFormData(prev => ({
      ...prev,
      goldType: goldItem.goldType,
      quantity: goldItem.quantity.toString(),
      grossWeight: goldItem.grossWeight.toString(),
      netWeight: goldItem.netWeight.toString(),
      goldValuePerGram: goldItem.goldValuePerGram.toString(),
              totalGoldValueGross: (goldItem.grossWeight * goldItem.goldValuePerGram).toString(),
              totalGoldValueNet: (goldItem.netWeight * goldItem.goldValuePerGram).toString()
    }));
    
    // Set edit mode and store the ID
    setEditingGoldId(goldItem.id);
    setIsEditMode(true);
  };

  const handleCancelEdit = () => {
    // Clear form and exit edit mode
    setFormData(prev => ({
      ...prev,
      goldType: '',
      quantity: '',
      grossWeight: '',
      netWeight: '',
      goldValuePerGram: '',
      totalGoldValueGross: '',
      totalGoldValueNet: ''
    }));
    
    setEditingGoldId(null);
    setIsEditMode(false);
    setErrors({});
  };

  const handleDeleteDirect = (goldItem) => {
    
    handleDelete(goldItem.id);
  };

  const handleDelete = async (goldId) => {
    setIsDeleting(true);
    setDeletingGoldId(goldId);
    
    try {
      const accessToken = localStorage.getItem('accessToken');
      
      if (!accessToken) {
        setModalConfig({
          title: 'Error',
          message: 'Access token not found. Please login again.',
          type: 'error'
        });
        setShowModal(true);
        return;
      }

      

      const response = await fetch(buildApiUrl(API_ENDPOINTS.GOLD_DELETE(goldId)), {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Accept': '*/*',
          'Content-Type': 'application/json'
        }
      });

      

      if (response.ok) {
        // Parse the response as text since API returns just "1"
        const responseText = await response.text();

        
        // API returns "1" for successful deletion
        if (responseText.trim() === '1') {
          setModalConfig({
            title: 'Deleted',
            message: 'Gold information has been deleted successfully!',
            type: 'success'
          });
          setShowModal(true);
          
          // If the deleted item was being edited, clear the form
          if (editingGoldId === goldId) {
            handleCancelEdit();
          }
          
          // Refresh the gold info list after a short delay
          setTimeout(() => {
            fetchGoldInfo();
          }, 500);
        } else {
          console.error('Unexpected delete response:', responseText);
          setModalConfig({
            title: 'Error',
            message: `Delete operation failed. Server response: ${responseText}`,
            type: 'error'
          });
          setShowModal(true);
        }
      } else {
        const errorData = await response.text();
        console.error('Delete API Error:', response.status, response.statusText, errorData);
        setModalConfig({
          title: 'Error',
          message: `Failed to delete gold information. Error: ${response.status}`,
          type: 'error'
        });
        setShowModal(true);
      }
    } catch (error) {
      console.error('Delete Network Error:', error);
      setModalConfig({
        title: 'Error',
        message: 'Network error. Please check your connection and try again.',
        type: 'error'
      });
      setShowModal(true);
    } finally {
      setIsDeleting(false);
      setDeletingGoldId(null);
    }
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const accessToken = localStorage.getItem('accessToken');
      
      if (!accessToken) {
        setModalConfig({
          title: 'Error',
          message: 'Access token not found. Please login again.',
          type: 'error'
        });
        setShowModal(true);
        setIsSubmitting(false);
        return;
      }

      // Use loanId if in edit mode, otherwise use generatedId
      const currentLoanId = editLoan ? loanId : generatedId;
      const { totalGoldValueGross, totalGoldValueNet } = calculateTotalValues();

      const payload = {
        id: isEditMode ? editingGoldId : 0,
        clientID: 0,
        loanID: currentLoanId,
        goldType: formData.goldType,
        quantity: parseInt(formData.quantity),
        grossWeight: parseFloat(formData.grossWeight),
        netWeight: parseFloat(formData.netWeight),
        goldValuePerGram: parseFloat(formData.goldValuePerGram),
        totalValue: totalGoldValueNet, // Use net value as total value
        lastActionDate: new Date().toISOString(),
        lastActionUserID: 0
      };



      const response = await fetch(buildApiUrl(API_ENDPOINTS.GOLD_SAVE), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        setModalConfig({
          title: 'Success',
          message: isEditMode ? 'Gold information updated successfully!' : 'Gold information saved successfully!',
          type: 'success'
        });
        setShowModal(true);
        
        // Clear form and reset edit mode
        setFormData(prev => ({
          ...prev,
          goldType: '',
          quantity: '',
          grossWeight: '',
          netWeight: '',
          goldValuePerGram: '',
          totalGoldValueGross: '',
          totalGoldValueNet: ''
        }));
        
        setEditingGoldId(null);
        setIsEditMode(false);
        
        // Refresh the gold info list
        fetchGoldInfo();
      } else {
        const errorData = await response.json();
        setModalConfig({
          title: 'Error',
          message: `Failed to ${isEditMode ? 'update' : 'save'} gold information. Please try again.`,
          type: 'error'
        });
        setShowModal(true);
      }
    } catch (error) {
      setModalConfig({
        title: 'Error',
        message: 'Network error. Please check your connection and try again.',
        type: 'error'
      });
      setShowModal(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        {!hideTitle && <h2 className="text-2xl font-bold text-gray-900">Gold Info</h2>}
        {isEditMode && (
          <div className="flex items-center space-x-2">
            <span className="bg-blue-100 text-indigo-900 px-3 py-1 rounded-full text-sm font-medium">
              Editing Gold Item
            </span>
            <button
              onClick={handleCancelEdit}
              className="text-gray-500 hover:text-gray-700 px-3 py-1 border border-gray-300 rounded-md text-sm"
            >
              Cancel Edit
            </button>
          </div>
        )}
      </div>

      {/* Validation Error Message */}
      {validationError && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-800">{validationError}</p>
            </div>
          </div>
        </div>
      )}

      {/* Gold Info Table */}
      {goldInfoList.length > 0 && (
        <div className="mb-8">
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Gold Item Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quantity</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Gross Weight</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Net Weight</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Gold Value per Gram</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Value (Gross)</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Value (Net)</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {goldInfoList.map((item, index) => (
                  <tr key={item.id} className={editingGoldId === item.id ? 'bg-blue-50' : ''}>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{item.goldType}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{item.quantity}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{item.grossWeight} grams</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{item.netWeight} grams</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{item.goldValuePerGram}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {(item.grossWeight * item.goldValuePerGram).toFixed(2)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {(item.netWeight * item.goldValuePerGram).toFixed(2)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center space-x-2">
                        <button 
                          onClick={() => handleEdit(item)}
                          className={`text-indigo-900 hover:text-blue-700 p-1 rounded transition-colors ${
                            editingGoldId === item.id ? 'bg-indigo-100' : ''
                          }`}
                          title="Edit gold item"
                          disabled={isDeleting && deletingGoldId === item.id}
                        >
                           <Edit size={16} />
                        </button>
                        <button 
                          onClick={() => handleDeleteDirect(item)}
                          className={`text-red-600 hover:text-red-900 p-1 rounded transition-colors ${
                            isDeleting && deletingGoldId === item.id ? 'opacity-50 cursor-not-allowed' : 'hover:bg-red-50'
                          }`}
                          title="Delete gold item"
                          disabled={isDeleting && deletingGoldId === item.id}
                        >
                          {isDeleting && deletingGoldId === item.id ? '⏳' : <Trash2 size={16} />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Gold Item Type <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.goldType || ''}
            onChange={(e) => handleInputChange('goldType', e.target.value)}
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none ${
              errors.goldType ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Gold Ring, Chain, Necklace"
          />
          {errors.goldType && (
            <p className="text-red-500 text-sm mt-1">{errors.goldType}</p>
          )}
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Quantity <span className="text-red-500">*</span>
          </label>
          <input
            type="number"    
            onWheel={(e) => e.target.blur()}
            value={formData.quantity || ''}
            onChange={(e) => handleInputChange('quantity', e.target.value)}
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none ${
              errors.quantity ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder=" 1"
            min="1"
          />
          {errors.quantity && (
            <p className="text-red-500 text-sm mt-1">{errors.quantity}</p>
          )}
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Gross Weight (grams) <span className="text-red-500">*</span>
          </label>
          <input
            type="number"    
            onWheel={(e) => e.target.blur()}
            step="0.01"
            value={formData.grossWeight || ''}
            onChange={(e) => handleInputChange('grossWeight', e.target.value)}
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none ${
              errors.grossWeight ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="12.00"
          />
          {errors.grossWeight && (
            <p className="text-red-500 text-sm mt-1">{errors.grossWeight}</p>
          )}
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Net Weight (grams) <span className="text-red-500">*</span>
          </label>
          <input
            type="number"    
            onWheel={(e) => e.target.blur()}
            step="0.01"
            value={formData.netWeight || ''}
            onChange={(e) => handleInputChange('netWeight', e.target.value)}
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none ${
              errors.netWeight ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="10.50"
          />
          {errors.netWeight && (
            <p className="text-red-500 text-sm mt-1">{errors.netWeight}</p>
          )}
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Gold Value per Gram <span className="text-red-500">*</span>
          </label>
          <input
            type="number"    
            onWheel={(e) => e.target.blur()}
            step="0.01"
            value={formData.goldValuePerGram || ''}
            onChange={(e) => handleInputChange('goldValuePerGram', e.target.value)}
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none ${
              errors.goldValuePerGram ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="5000.00"
          />
          {errors.goldValuePerGram && (
            <p className="text-red-500 text-sm mt-1">{errors.goldValuePerGram}</p>
          )}
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Total Gold Value (Gross)
          </label>
          <input
            type="text"
            value={formData.totalGoldValueGross || ''}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-700"
            readOnly
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Total Gold Value (Net)
          </label>
          <input
            type="text"
            value={formData.totalGoldValueNet || ''}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-700"
            readOnly
          />
        </div>
      </div>

      <div className="flex justify-end mt-6 space-x-3">
        {isEditMode && (
          <button
            onClick={handleCancelEdit}
            className="px-6 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
          >
            Cancel
          </button>
        )}
        <button
          onClick={handleSave}
          disabled={isSubmitting}
          className="px-6 py-2 bg-indigo-900 text-white rounded-lg hover:bg-indigo-700 transition-colors"
        >
          {isSubmitting ? (isEditMode ? 'Updating...' : 'Adding...') : (isEditMode ? 'Update' : 'Add')}
        </button>
      </div>

      {/* Navigation Buttons - Only show if not in edit mode and hideNavigation is not true */}
      {!hideNavigation && (
        <div className="flex justify-between mt-6">
          <button
            onClick={onPrev}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Back
          </button>
          
          <button
            onClick={handleNext}
            disabled={!isValidForNext}
            className={`px-6 py-2 rounded-lg transition-colors ${
              isValidForNext 
                ? 'bg-indigo-900 text-white hover:bg-indigo-700' 
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};
// Loan Application Form
const LoanApplicationForm = ({ formData, setFormData, generatedId, setShowModal, setModalConfig, editLoan, loanId, onNext, hideTitle }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [loanInterestId, setLoanInterestId] = useState(null);
  const [dataFetched, setDataFetched] = useState(false);
  const [localFormData, setLocalFormData] = useState({}); // Local state to prevent external interference
  const fetchID = loanId || generatedId;

  // Initialize local form data from props
  useEffect(() => {
    if (!editLoan || !dataFetched) {
      setLocalFormData(formData);
    }
  }, [formData, editLoan, dataFetched]);

  // Helper function to format date to YYYY-MM-DD for input
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    
    let dateToFormat;
    
    if (dateString.includes('T')) {
      dateToFormat = dateString.split('T')[0];
    } else {
      dateToFormat = dateString;
    }
    
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateToFormat)) {
      return '';
    }
    
    return dateToFormat;
  };

  // Helper function to calculate due date based on start date and loan term
  const calculateDueDate = (startDate, loanTermMonths) => {
    if (!startDate || !loanTermMonths) return '';
    
    const [year, month, day] = startDate.split('-').map(num => parseInt(num));
    
    const start = new Date(year, month - 1, day);
    if (isNaN(start.getTime())) return '';
    
    const dueDate = new Date(start);
    dueDate.setMonth(dueDate.getMonth() + parseInt(loanTermMonths));
    
    const dueDateYear = dueDate.getFullYear();
    const dueDateMonth = String(dueDate.getMonth() + 1).padStart(2, '0');
    const dueDateDay = String(dueDate.getDate()).padStart(2, '0');
    
    return `${dueDateYear}-${dueDateMonth}-${dueDateDay}`;
  };

  // Update due date when start date or loan term changes
  useEffect(() => {
    const currentData = dataFetched ? localFormData : formData;
    if (currentData.loanStartDate && currentData.loanTerm) {
      const calculatedDueDate = calculateDueDate(currentData.loanStartDate, currentData.loanTerm);
      if (calculatedDueDate && calculatedDueDate !== currentData.loanDueDate) {
        const updatedData = {
          ...currentData,
          loanDueDate: calculatedDueDate
        };
        
        if (dataFetched) {
          setLocalFormData(updatedData);
        } else {
          setFormData(updatedData);
        }
      }
    }
  }, [localFormData.loanStartDate, localFormData.loanTerm, formData.loanStartDate, formData.loanTerm, dataFetched]);

  // Fetch loan data when editing
  const fetchLoanData = useCallback(async () => {
    if (!fetchID || dataFetched) return;
    
    setIsLoadingData(true);
    try {
      const accessToken = localStorage.getItem('accessToken');

      if (!accessToken) {
        alert('Access token not found. Please login again.');
        return;
      }

      const response = await fetch(buildApiUrl(API_ENDPOINTS.LOAN_INTEREST_DETAILS(fetchID)), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        }
      });

      if (response.ok) {
        const result = await response.json();


        // Store the loan interest ID for updates
        setLoanInterestId(result.id);

        // Create the updated form data object
        const updatedFormData = {
          loanAmount: result.loanAmount?.toString() || '',
          interestRate: result.interestRate?.toString() || '',
          penalInterestRate: result.interestRateAfterMissedDueDate?.toString() || '',
          loanStartDate: formatDateForInput(result.loanDate) || '',
          loanDueDate: formatDateForInput(result.dueDate) || '',
          loanTerm: result.loanTerm?.toString() || ''
        };



        // Update both local and parent form data
        setLocalFormData(prev => ({
          ...prev,
          ...updatedFormData
        }));

        setFormData(prev => ({
          ...prev,
          ...updatedFormData
        }));

        // Mark data as fetched
        setDataFetched(true);

      } else {
        const errorData = await response.json();
        setModalConfig({
          title: 'Error',
          message: `Error fetching loan data: ${errorData.message || 'Unknown error'}`,
          type: 'error'
        });
        setShowModal(true);
      }
    } catch (error) {
      setModalConfig({
        title: 'Error',
        message: 'Network error occurred while fetching loan data',
        type: 'error'
      });
      setShowModal(true);
    } finally {
      setIsLoadingData(false);
    }
  }, [fetchID, setFormData, setModalConfig, setShowModal, dataFetched]);

  // Fetch data when in edit mode and loanId is available
  useEffect(() => {
    if (editLoan && fetchID && !dataFetched) {
      fetchLoanData();
    }
  }, [editLoan, fetchID, fetchLoanData, dataFetched]);

  // Reset state when switching modes
  useEffect(() => {
    if (!editLoan) {
      setDataFetched(false);
      setLoanInterestId(null);
      setLocalFormData({});
    }
  }, [editLoan, loanId]);

  // Validation function
  const validateForm = () => {
    const currentData = dataFetched ? localFormData : formData;
    const newErrors = {};

    if (!currentData.loanAmount || currentData.loanAmount <= 0) {
      newErrors.loanAmount = 'Loan amount is required and must be greater than 0';
    }

    if (!currentData.interestRate || currentData.interestRate <= 0) {
      newErrors.interestRate = 'Interest rate is required and must be greater than 0';
    }

    if (!currentData.penalInterestRate || currentData.penalInterestRate <= 0) {
      newErrors.penalInterestRate = 'Penal interest rate is required and must be greater than 0';
    }

    if (!currentData.loanTerm || currentData.loanTerm <= 0) {
      newErrors.loanTerm = 'Loan term is required and must be greater than 0';
    }

    if (!currentData.loanStartDate) {
      newErrors.loanStartDate = 'Loan start date is required';
    }

    if (!currentData.loanDueDate) {
      newErrors.loanDueDate = 'Loan due date is required';
    }

    if (currentData.loanStartDate && currentData.loanDueDate) {
      const startDate = new Date(currentData.loanStartDate);
      const dueDate = new Date(currentData.loanDueDate);

      if (dueDate <= startDate) {
        newErrors.loanDueDate = 'Loan due date must be after the loan start date';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form field changes
  const handleFieldChange = (field, value) => {
    const updatedData = { [field]: value };
    
    // If loan start date or loan term changes, calculate due date immediately
    if ((field === 'loanStartDate' || field === 'loanTerm') && value) {
      const currentData = dataFetched ? localFormData : formData;
      const startDate = field === 'loanStartDate' ? value : currentData.loanStartDate;
      const loanTerm = field === 'loanTerm' ? value : currentData.loanTerm;
      
      if (startDate && loanTerm) {
        const calculatedDueDate = calculateDueDate(startDate, loanTerm);
        if (calculatedDueDate) {
          updatedData.loanDueDate = calculatedDueDate;
        }
      }
    }
    
    if (dataFetched) {
      setLocalFormData(prev => ({ ...prev, ...updatedData }));
    }
    
    setFormData(prev => ({ ...prev, ...updatedData }));
  };

  // Save and proceed to next step function
  const saveAndNext = async () => {
    if (!validateForm()) {
      return;
    }

    const currentLoanId = editLoan ? loanId : generatedId;
    const currentData = dataFetched ? localFormData : formData;
    
    if (!currentLoanId) {
      alert('Loan ID is required to save the loan interest');
      return;
    }

    setIsLoading(true);

    try {
      const accessToken = localStorage.getItem('accessToken');
      
      if (!accessToken) {
        alert('Access token not found. Please login again.');
        return;
      }

      // Ensure loanInterestId is a valid number
      const currentLoanInterestId = editLoan && loanInterestId ? parseInt(loanInterestId) : 0;
      
      const payload = {
        id: currentLoanInterestId,
        clientID: 0,
        loanID: currentLoanId,
        loanAmount: parseFloat(currentData.loanAmount),
        loanDate: new Date(currentData.loanStartDate).toISOString(),
        loanTerm: parseFloat(currentData.loanTerm),
        dueDate: new Date(currentData.loanDueDate).toISOString(),
        interestRate: parseFloat(currentData.interestRate),
        interestRateAfterMissedDueDate: parseFloat(currentData.penalInterestRate),
        lastActionDate: new Date().toISOString(),
        lastActionUserID: 0
      };



      const response = await fetch(buildApiUrl(API_ENDPOINTS.LOAN_SAVE_INTEREST), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        setModalConfig({
          title: 'Success',
          message: editLoan ? 'Loan interest updated successfully!' : 'Loan interest saved successfully!',
          type: 'success'
        });
        setShowModal(true);
        
        if (onNext) {
          onNext();
        }
      } else {
        const errorData = await response.json();
        setModalConfig({
          title: 'Error',
          message: `Error ${editLoan ? 'updating' : 'saving'} loan interest: ${errorData.message || 'Unknown error'}`,
          type: 'error'
        });
        setShowModal(true);
      }
    } catch (error) {
      setModalConfig({
        title: 'Error',
        message: `Network error occurred while ${editLoan ? 'updating' : 'saving'} loan interest`,
        type: 'error'
      });
      setShowModal(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Get current form data to display
  const getCurrentFormData = () => {
    return dataFetched ? localFormData : formData;
  };

  // Calculate interest amount based on loan amount, interest rate, and loan term
  const calculateInterestAmount = () => {
    const loanAmount = parseFloat(currentFormData.loanAmount) || 0;
    const interestRate = parseFloat(currentFormData.interestRate) || 0;
    const loanTerm = parseFloat(currentFormData.loanTerm) || 0;
    
    if (loanAmount > 0 && interestRate > 0 && loanTerm > 0) {
      return (loanAmount * interestRate * loanTerm) / (12 * 100);
    }
    return 0;
  };

  const currentFormData = getCurrentFormData();

  // Show loading spinner while fetching data
  if (isLoadingData) {
    return (
      <div className="space-y-6">
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          <span className="ml-3 text-gray-600">Loading loan data...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {!hideTitle && <h2 className="text-2xl font-bold text-gray-900 mb-6">
        {editLoan ? 'Edit Loan Info' : 'Loan Info'}
      </h2>}
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Loan Amount <span className="text-red-500">*</span>
          </label>
          <input
            type="number"    
            onWheel={(e) => e.target.blur()}
            value={currentFormData.loanAmount || ''}
            onChange={(e) => handleFieldChange('loanAmount', e.target.value)}
            placeholder="Enter loan amount"
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none ${
              errors.loanAmount ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.loanAmount && (
            <p className="text-red-500 text-sm mt-1">{errors.loanAmount}</p>
          )}
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Interest Rate (%)<span className="text-red-500">*</span>
          </label>
          <input
            type="number"    
            onWheel={(e) => e.target.blur()}
            inputMode="decimal"
            value={currentFormData.interestRate || ''}
            placeholder="12%"
            onChange={(e) => handleFieldChange('interestRate', e.target.value)}
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none ${
              errors.interestRate ? 'border-red-500' : 'border-gray-300'
            } no-spinner`}
          />
          {errors.interestRate && (
            <p className="text-red-500 text-sm mt-1">{errors.interestRate}</p>
          )}
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Interest Amount (Auto-calculated)
          </label>
          <input
            type="number"
            value={calculateInterestAmount().toFixed(2)}
            readOnly
            className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed outline-none"
            placeholder="Auto-calculated"
          />
          <p className="text-xs text-gray-500 mt-1">
            Calculated as: (Loan Amount × Interest Rate × Loan Term) ÷ (12 × 100)
          </p>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Total Amount (Auto-calculated)
          </label>
          <input
            type="number"
            value={((parseFloat(currentFormData.loanAmount) || 0) + calculateInterestAmount()).toFixed(2)}
            readOnly
            className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed outline-none"
            placeholder="Auto-calculated"
          />
          <p className="text-xs text-gray-500 mt-1">
            Loan Amount + Interest Amount
          </p>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Loan Term (Months) <span className="text-red-500">*</span>
          </label>
          <input
            type="number"    
            onWheel={(e) => e.target.blur()}
            min="1"
            value={currentFormData.loanTerm || ''}
            onChange={(e) => handleFieldChange('loanTerm', e.target.value)}
            placeholder="Enter loan term in months"
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none ${
              errors.loanTerm ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.loanTerm && (
            <p className="text-red-500 text-sm mt-1">{errors.loanTerm}</p>
          )}
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Penal Interest Rate(%) for missed Payments (Installment) <span className="text-red-500">*</span>
          </label>
          <input
            type="number"    
            onWheel={(e) => e.target.blur()}
            step="0.01"
            value={currentFormData.penalInterestRate || ''}
            placeholder="18%"
            onChange={(e) => handleFieldChange('penalInterestRate', e.target.value)}
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none ${
              errors.penalInterestRate ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.penalInterestRate && (
            <p className="text-red-500 text-sm mt-1">{errors.penalInterestRate}</p>
          )}
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Loan Start Date <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            value={currentFormData.loanStartDate || ''}
            onChange={(e) => handleFieldChange('loanStartDate', e.target.value)}
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none ${
              errors.loanStartDate ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.loanStartDate && (
            <p className="text-red-500 text-sm mt-1">{errors.loanStartDate}</p>
          )}
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Loan Due Date <span className="text-red-500">*</span> (Auto-calculated)
          </label>
          <input
            type="date"
            value={currentFormData.loanDueDate || ''}
            readOnly
            className={`w-full px-4 py-3 border rounded-lg bg-gray-100 cursor-not-allowed outline-none ${
              errors.loanDueDate ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.loanDueDate && (
            <p className="text-red-500 text-sm mt-1">{errors.loanDueDate}</p>
          )}
          {currentFormData.loanStartDate && currentFormData.loanTerm && !currentFormData.loanDueDate && (
            <p className="text-blue-600 text-sm mt-1">⏳ Calculating due date...</p>
          )}
          <p className="text-xs text-gray-500 mt-1">
            Due date is automatically calculated based on start date and loan term
          </p>
        </div>
      </div>

      <div className="flex justify-end mt-8">
        <button
          type="button"
          onClick={() => window.history.back()}
          className="px-6 py-3 bg-gray-500 mr-3 text-white rounded-lg hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={saveAndNext}
          disabled={isLoading}
          className={`px-6 py-3 rounded-lg font-medium transition-colors ${
            isLoading
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-indigo-900 hover:bg-indigo-700 text-white'
          }`}
        >
          {isLoading ? (editLoan ? 'Updating...' : 'Saving...') : (editLoan ? 'Update' : 'Submit')}
        </button>
      </div>


    </div>
  );
};

// Photo Upload Form
const PhotoUploadForm = ({ formData, setFormData }) => {
  const loanInfo = {
    loanNumber: '#2466',
    name: 'Selvam',
    mobile: '9876543210',
    loanAmount: '50000',
    interestAmount: '6000',
    total: '56000',
    interestRate: '10%',
    penalInterest: '2%',
    gold: 'Gold Ring',
    weight: 'Gross 12 grams, Net 10 gram'
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Photo Upload</h2>
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
          <div className="flex justify-center mb-4">
            <Camera size={48} className="text-gray-400" />
          </div>
          <button className="px-6 py-2 bg-indigo-900 text-white rounded-lg hover:bg-indigo-700 transition-colors">
            Upload
          </button>
        </div>
      </div>

      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Loan Info</h2>
        <div className="space-y-3 text-sm">
          <div><span className="font-medium">Loan Number</span> - {loanInfo.loanNumber}</div>
          <div><span className="font-medium">Name</span> - {loanInfo.name}</div>
          <div><span className="font-medium">Mobile Number</span> - {loanInfo.mobile}</div>
          <div><span className="font-medium">Loan Amount</span> - {loanInfo.loanAmount}</div>
          <div><span className="font-medium">Interest Amount</span> - {loanInfo.interestAmount}</div>
          <div><span className="font-medium">Total</span> - {loanInfo.total}</div>
          <div><span className="font-medium">Interest Rate</span> - {loanInfo.interestRate}</div>
          <div><span className="font-medium">Penal Interest</span> - {loanInfo.penalInterest}</div>
          <div><span className="font-medium">Gold</span> - {loanInfo.gold}</div>
          <div><span className="font-medium">Weight</span> - {loanInfo.weight}</div>
        </div>
      </div>
    </div>
  );
};

const ConfirmationForm = ({ generatedId, editLoan, loanId }) => {
  const [loanData, setLoanData] = useState(null);
  const [interestData, setInterestData] = useState(null);
  const [goldData, setGoldData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const receiptRef = useRef();
  const companyUseRef = useRef();
  const customerUseRef = useRef();

  // Get the effective loan ID - use loanId if in edit mode, otherwise use generatedId
  const effectiveLoanId = editLoan ? loanId : generatedId;

  const fetchLoanData = useCallback(async () => {
    // Only fetch if we have an effective loan ID
    if (!effectiveLoanId) {
      setError('No loan ID provided');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const loanResponse = await fetch(buildApiUrl(API_ENDPOINTS.LOAN_GET_BY_ID(effectiveLoanId)));
      const loan = await loanResponse.json();

      const interestResponse = await fetch(buildApiUrl(API_ENDPOINTS.LOAN_INTEREST_DETAILS(effectiveLoanId)));
      const interest = await interestResponse.json();

      const goldResponse = await fetch(buildApiUrl(API_ENDPOINTS.GOLD_GET_BY_LOAN_ID(effectiveLoanId)));
      const gold = await goldResponse.json();

      setLoanData(loan);
      setInterestData(interest);
      setGoldData(gold);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  }, [effectiveLoanId]);

  useEffect(() => {
    if (effectiveLoanId) {
      fetchLoanData();
    } else {
      setLoading(false);
      setError('No loan ID provided');
    }
  }, [effectiveLoanId, fetchLoanData]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  const calculateTotal = () => {
    return goldData.reduce((sum, item) => sum + item.totalValue, 0);
  };

  // Enhanced PDF download function with proper two-page layout
const handleDownloadPDF = async () => {
    try {
      setIsGeneratingPDF(true);
      
      // Get both sections
      const companyUseElement = companyUseRef.current;
      const customerUseElement = customerUseRef.current;
      
      if (!companyUseElement || !customerUseElement) {
        throw new Error('Receipt sections not found');
      }
      
      // PDF settings
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const margin = 10;
      const contentWidth = pdfWidth - (margin * 2);
      
      // Configure html2canvas options for better quality
      const canvasOptions = {
        scale: 2, // Reduced scale for better performance
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false,
        removeContainer: false,
        imageTimeout: 15000,
        foreignObjectRendering: false, // Changed to false for better compatibility
        letterRendering: true,
        scrollX: 0,
        scrollY: 0,
        x: 0,
        y: 0
      };
      
      // Ensure elements are visible and styled properly before capturing
      const originalCompanyDisplay = companyUseElement.style.display;
      const originalCustomerDisplay = customerUseElement.style.display;
      
      // Make sure elements are visible and add extra padding for borders
      companyUseElement.style.display = 'block';
      companyUseElement.style.visibility = 'visible';
      companyUseElement.style.opacity = '1';
      companyUseElement.style.paddingBottom = '20px'; // Add extra padding for bottom border
      
      customerUseElement.style.display = 'block';
      customerUseElement.style.visibility = 'visible';
      customerUseElement.style.opacity = '1';
      customerUseElement.style.paddingBottom = '20px'; // Add extra padding for bottom border
      
      // Wait a moment for rendering
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Generate canvas for Company Use section
      //console.log('Generating company section canvas...');
      const companyCanvas = await html2canvas(companyUseElement, {
        ...canvasOptions,
        width: companyUseElement.offsetWidth,
        height: companyUseElement.offsetHeight + 20, // Add extra height for border
      });
      
      if (companyCanvas.width === 0 || companyCanvas.height === 0) {
        throw new Error('Company section canvas is empty');
      }
      
      const companyImgData = companyCanvas.toDataURL('image/png', 0.95);
      
      // Calculate dimensions for company section with border space
      const companyImgWidth = contentWidth;
      const companyImgHeight = (companyCanvas.height * companyImgWidth) / companyCanvas.width;
      
      // Add company section to first page
      pdf.addImage(companyImgData, 'PNG', margin, margin, companyImgWidth, companyImgHeight);
      
      // Add new page for customer section
      pdf.addPage();
      
      // Generate canvas for Customer Use section
      //console.log('Generating customer section canvas...');
      const customerCanvas = await html2canvas(customerUseElement, {
        ...canvasOptions,
        width: customerUseElement.offsetWidth,
        height: customerUseElement.offsetHeight + 20, // Add extra height for border
      });
      
      if (customerCanvas.width === 0 || customerCanvas.height === 0) {
        throw new Error('Customer section canvas is empty');
      }
      
      const customerImgData = customerCanvas.toDataURL('image/png', 0.95);
      
      // Calculate dimensions for customer section with border space
      const customerImgWidth = contentWidth;
      const customerImgHeight = (customerCanvas.height * customerImgWidth) / customerCanvas.width;
      
      // Add customer section to second page
      pdf.addImage(customerImgData, 'PNG', margin, margin, customerImgWidth, customerImgHeight);
      
      // Restore original display styles
      companyUseElement.style.display = originalCompanyDisplay;
      customerUseElement.style.display = originalCustomerDisplay;
      companyUseElement.style.paddingBottom = ''; // Reset padding
      customerUseElement.style.paddingBottom = ''; // Reset padding
      
      // Save the PDF
      const fileName = `loan_receipt_${generatedId || loanId}_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);
      
      //console.log('PDF generated successfully:', fileName);
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert(`Error generating PDF: ${error.message}. Please try again.`);
    } finally {
      setIsGeneratingPDF(false);
    }
  };

// Enhanced print function with better cross-browser compatibility and page break control
const handlePrint = () => {
  // Create a new window for printing
  const printWindow = window.open('', '_blank');
  
  // Get the receipt content
  const receiptContent = receiptRef.current.innerHTML;
  
  // Create complete HTML document with highly optimized inline styles
  const printHTML = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>VinayagaLoan Receipt - ${generatedId ||loanId}</title>
      <style>
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        
        body {
          font-family: 'Courier New', monospace;
          font-size: 17px; /* Reduced from 16.10px */
          line-height: 1.3; /* Reduced from 1.4 */
          color: #000;
          background: #fff;
          margin: 15px; /* Reduced from 20px */
        }
        
        .receipt-container {
          background: white;
          border: 2px solid #000;
          padding: 15px; /* Reduced from 20px */
          max-width: 100%;
          margin: 0 auto;
        }
        
        /* Header styles */
        .text-center {
          text-align: center;
        }
        
        .text-2xl {
          font-size: 24px; /* Reduced from 24px */
          font-weight: bold;
        }
        
        .text-lg {
          font-size: 17px; /* Reduced from 18px */
          font-weight: 600;
        }

        /* Logo styles - OPTIMIZED */
        .logo-img {
          width: 12rem; /* Reduced from 15.5rem */
          height: 4.5rem; /* Reduced from 6rem */
          margin: 0 auto 0.25rem auto; /* Minimal bottom margin */
          border-radius: 0.5rem; /* Reduced border radius */
          box-shadow: none; /* Removed shadow for print */
          display: block;
        }
        
        .mb-6 {
          margin-bottom: 18px; /* Reduced from 24px */
        }
        
        .mb-4 {
          margin-bottom: 12px; /* Reduced from 16px */
        }
        
        .mb-2 {
          margin-bottom: 6px; /* Reduced from 8px */
        }

        .mb-1 {
          margin-bottom: 3px; /* Reduced from 4px */
        }
        
        .pb-4 {
          padding-bottom: 10px; /* Reduced from 16px */
        }
        
        .border-b-2 {
          border-bottom: 2px solid #000;
        }
        
        /* Grid layout */
        .grid {
          display: table;
          width: 100%;
          border-collapse: collapse;
        }
        
        .grid-cols-2 {
          display: table;
          width: 100%;
        }
        
        .grid-cols-2 > div {
          display: table-cell;
          width: 50%;
          vertical-align: top;
          padding-right: 15px; /* Reduced from 20px */
        }
        
        .grid-cols-2 > div:last-child {
          padding-right: 0;
        }
        
        .gap-6 {
          border-spacing: 18px 0; /* Reduced from 24px */
        }
        
        .gap-12 {
          border-spacing: 36px 0; /* Reduced from 48px */
        }
        
        /* Table styles - OPTIMIZED for more rows */
        table {
          width: 100%;
          border-collapse: collapse;
          border: 2px solid #000;
          margin-bottom: 15px; /* Reduced from 24px */
          table-layout: fixed;
        }
        
        th, td {
          border: 1px solid #000;
          padding: 4px; /* Reduced from 8px */
          text-align: left;
          word-wrap: break-word;
          font-size: 16px; /* Specific smaller font for table */
        }
        
        th {
          background-color: #f0f0f0;
          font-weight: bold;
          text-align: center;
          padding: 5px 4px; /* Slightly more padding for headers */
        }
        
        /* CRITICAL: Adjusted column widths to prevent specific wrapping issues */
        th:first-child,
        td:first-child {
          width: 22%; /* Back to original */
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          min-width: 100px;
        }
        
        /* Other column widths - specifically target problem columns */
        th:nth-child(2), td:nth-child(2) { 
          width: 22%; /* Further increased to accommodate எண்ணிக்கை */
          white-space: nowrap; /* Only prevent எண்ணிக்கை wrapping */
        }
        th:nth-child(3), td:nth-child(3) { width: 18%; }
        th:nth-child(4), td:nth-child(4) { width: 18%; }
        th:nth-child(5), td:nth-child(5) { 
          width: 21%; 
          white-space: nowrap; /* Only prevent மொத்த மதிப்பு values wrapping */
        }
        
        .text-center {
          text-align: center;
        }
        
        .text-right {
          text-align: right;
        }
        
        .text-left {
          text-align: left;
        }
        
        .capitalize {
          text-transform: capitalize;
        }
        
        .font-bold {
          font-weight: bold;
        }
        
        .bg-gray-100 {
          background-color: #f0f0f0;
        }
        
        /* Signature section - COMPACT */
        .mt-10 {
          margin-top: 28px; /* Reduced from 36px */
        }
        
        .mt-8 {
          margin-top: 16px; /* Reduced from 16px */
        }
        
        .pt-2 {
          padding-top: 6px; /* Reduced from 8px */
        }
        
        .border-t-2 {
          border-top: 2px solid #000;
        }
        
        .text-xs {
          font-size: 11px; /* Reduced from 11px */
        }
        
        .text-gray-600 {
          color: #666;
        }

        /* Dotted line separator - MINIMAL */
        .dotted-separator {
          border: none;
          border-top: 2px dotted #000; /* Reduced from 3px */
          margin: 10px 0; /* Reduced from 20px */
          width: 100%;
        }

        .section-header {
          text-align: center;
          font-weight: bold;
          font-size: 10px; /* Reduced from 10px */
          margin: 8px 0; /* Reduced from 15px */
          text-transform: uppercase;
        }

        /* Terms section - HIGHLY COMPRESSED */
        .terms-section {
          margin-top: 8px; /* Reduced from 15px */
          padding: 8px; /* Reduced from 12px */
          border: 1px solid #000;
          background-color: #f9f9f9;
        }

        .terms-title {
          font-weight: bold;
          text-align: center;
          margin-bottom: 6px; /* Reduced from 12px */
          font-size: 14px; /* Reduced from 14px */
        }

        .terms-list {
          list-style-type: none;
          padding: 0;
        }

        .terms-list li {
          margin-bottom: 3px; /* Reduced from 6px */
          padding-left: 15px; /* Reduced from 20px */
          position: relative;
          font-size: 16px; /* Much smaller font */
          line-height: 1.2; /* Tighter line height */
        }

        .terms-list li:before {
          content: "•";
          position: absolute;
          left: 0;
          font-weight: bold;
        }

        /* Page break styles - ENHANCED */
        .page-break {
          page-break-before: always;
        }

        /* Prevent page breaks within important sections */
        .receipt-section {
          page-break-inside: avoid;
        }

        .signature-section {
          page-break-inside: avoid;
          margin-top: 10px; /* Reduced further */
        }

        /* Footer with minimal margins */
        .footer-section {
          margin-top: 8px; /* Minimal top margin */
          page-break-inside: avoid;
        }
        
        /* Print specific styles - ULTRA OPTIMIZED */
        @media print {
          body {
            margin: 0;
            padding: 8px; /* Minimal padding */
            font-size: 18px !important; /* Force smaller font */
          }
          
          .receipt-container {
            border: 2px solid #000 !important;
            margin: 0;
            padding: 10px !important; /* Minimal padding */
          }
          
          /* Logo optimization for print - COMPACT */
          .logo-img {
            width: 10rem !important; /* Much smaller */
            height: 3.5rem !important; /* Much smaller */
            margin: 0 auto 0.25rem auto !important; /* Minimal margin */
            border-radius: 0.25rem !important;
            box-shadow: none !important;
            display: block !important;
          }
          
          /* Section header - MINIMAL spacing */
          .section-header {
            margin: 4px 0 !important; /* Minimal margins */
            font-size: 16px !important; /* Tiny font */
          }
          
          /* Header section optimization */
          .mb-4 {
            margin-bottom: 8px !important; /* Minimal spacing */
          }
          
          .mb-2 {
            margin-bottom: 4px !important;
          }
          
          .mb-1 {
            margin-bottom: 2px !important;
          }
          
          .pb-4 {
            padding-bottom: 6px !important;
          }
          
          .mb-6 {
            margin-bottom: 10px !important; /* Minimal spacing */
          }
          
          /* Table optimization - ULTRA COMPACT */
          table {
            border: 2px solid #000 !important;
            page-break-inside: auto !important; /* Allow breaking if needed */
            margin-bottom: 8px !important; /* Minimal margin */
            table-layout: fixed !important;
          }
          
          th, td {
            border: 1px solid #000 !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
            padding: 3px !important; /* Minimal padding */
            font-size: 20px !important; /* Smaller font */
            line-height: 1.1 !important; /* Tighter line height */
          }
          
          th {
            padding: 4px 3px !important; /* Slightly more for headers */
          }
          
          /* FIXED: Balanced table column optimization */
          th:first-child, td:first-child {
            width: 22% !important; /* Back to balanced width */
            white-space: wrap !important;
            overflow: hidden !important;
            text-overflow: ellipsis !important;
            min-width: 100px !important;
            max-width: 130px !important;
          }
          
          th:nth-child(2), td:nth-child(2) { 
            width: 23% !important; /* Further increased to accommodate எண்ணிக்கை */
            white-space: nowrap !important; /* Only prevent எண்ணிக்கை wrapping */
          }
          th:nth-child(3), td:nth-child(3) { width: 14% !important; }
          th:nth-child(4), td:nth-child(4) { width: 14% !important; }
          th:nth-child(5), td:nth-child(5) { 
            width: 28% !important; 
            white-space: nowrap !important; /* Only prevent மொத்த மதிப்பு values wrapping */
          }
          
          th {
            background-color: #f0f0f0 !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          
          .bg-gray-100 {
            background-color: #f0f0f0 !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }

          /* Terms section - ULTRA COMPRESSED for print */
          .terms-section {
            background-color: #f9f9f9 !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
            margin-top: 5px !important; /* Minimal margin */
            padding: 6px !important; /* Minimal padding */
          }
          
          .terms-title {
            font-size: 16px !important; /* Smaller */
            margin-bottom: 4px !important; /* Minimal */
          }
          
          .terms-list li {
            font-size: 16px !important; /* Very small */
            line-height: 1.1 !important; /* Very tight */
            margin-bottom: 2px !important; /* Minimal spacing */
            padding-left: 12px !important; /* Reduced padding */
          }

          .dotted-separator {
            border-top: 2px dotted #000 !important;
            margin: 6px 0 !important; /* Minimal separator margins */
          }

          /* Signature section spacing - ULTRA COMPACT */
          .mt-10 {
            margin-top: 24px !important; /* Much smaller */
          }
          
          .mt-8 {
            margin-top: 18px !important; /* Minimal */
          }
          
          .signature-section {
            page-break-inside: avoid !important;
            margin-top: 20px !important; /* Compact */
          }

          /* Footer optimization - MINIMAL */
          .footer-section {
            margin-top: 6px !important; /* Minimal margin */
            page-break-inside: avoid !important;
          }
          
          .footer-section p {
            font-size: 10px !important; /* Tiny footer text */
            line-height: 1.1 !important;
            margin-bottom: 1px !important;
          }
          
          /* Grid spacing optimization */
          .gap-6 {
            border-spacing: 12px 0 !important; /* Reduced */
          }
          
          .gap-12 {
            border-spacing: 24px 0 !important; /* Reduced */
          }
          
          /* Ensure borders are visible */
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
        }
        
        @page {
          margin: 0.3in; /* Minimal page margins */
          size: A4;
        }
      </style>
    </head>
    <body>
      <div>
        ${receiptContent}
      </div>
      <script>
        window.onload = function() {
          // Small delay to ensure styles are applied
          setTimeout(function() {
            window.print();
            window.onafterprint = function() {
              window.close();
            };
          }, 500);
        };
      </script>
    </body>
    </html>
  `;
  
  // Write content to new window
  printWindow.document.write(printHTML);
  printWindow.document.close();
};
  // Reusable Header Component
  const ReceiptHeader = () => (
    <div className="text-center mb-4 border-b-2 border-gray-900 pb-4">
      <img src={logo} alt="Logo" className="w-62 h-24 mx-auto mb-1 rounded-xl shadow-md logo-img" />
      <h2 className="text-lg font-semibold">GOLD LOAN RECEIPT / தங்க கடன் ரசீது</h2>
      <div className="mt-2">
        <div><strong>உரிமம் எண்:</strong> TN-2020250409176</div>
        <div><strong>தொலைபேசி எண் :</strong> +918838297542</div>
      </div>
    </div>
  );

  // Reusable Loan Details Component
  const LoanDetails = () => (
  <div className="grid grid-cols-2 gap-6 mb-6">
    <div>
      <div className="mb-2">
        <strong>கடன்தாரர் பெயர் :</strong> {loanData?.loanHolderName}
      </div>
      <div className="mb-2">
        <strong>தொலைபேசி எண்:</strong> {loanData?.mobileNumber}
      </div>
      <div className="mb-2">
        <strong>முகவரி:</strong> {loanData?.address}
      </div>
    </div>
    <div>
      <div className="mb-2">
        <strong>கடன் எண்:</strong> {loanData?.loanNumber}
      </div>
      <div className="mb-2">
        <strong>கடன் தேதி:</strong> {formatDate(interestData?.loanDate)}
      </div>
      <div className="mb-2">
        <strong>கடன் தொகை:</strong> {formatCurrency(interestData?.loanAmount)}
      </div>
      <div className="mb-2">
        <strong>வட்டி விகிதம்:</strong> {interestData?.interestRate}%
      </div>
      <div className="mb-2">
        <strong>1 கிராம் மதிப்பு:</strong> {formatCurrency(goldData[0]?.goldValuePerGram)}
      </div>
      {/* <div className="mb-2">
        <strong>கடன் திருப்பிச் செலுத்தும் தேதி:</strong> {formatDate(interestData?.dueDate)}
      </div>
      <div className="mb-2">
        <strong>கடன் காலம்:</strong> {interestData?.loanTerm} months
      </div>
      <div className="mb-2">
        <strong>தவணை தேதிக்கு பின் வட்டி விகிதம்:</strong> {interestData?.interestRateAfterMissedDueDate}%
      </div> */}
    </div>
  </div>
);

// Reusable Gold Items Table Component
const GoldItemsTable = () => {
  // Calculate gross weight total
  const calculateGrossWeightTotal = () => {
    return goldData.reduce((sum, item) => sum + parseFloat(item.grossWeight || 0), 0);
  };

  // Calculate net weight total
  const calculateNetWeightTotal = () => {
    return goldData.reduce((sum, item) => sum + parseFloat(item.netWeight || 0), 0);
  };

  // Calculate total count
  const calculateTotalCount = () => {
    return goldData.reduce((sum, item) => sum + parseInt(item.quantity || 0), 0);
  };

  return (
    <div className="border-2 border-gray-900 mb-6">
      <table className="w-full">
        <thead>
          <tr className="border-b-2 border-gray-900 bg-gray-100">
            <th className="border-r border-gray-900 p-2 text-left">நகை விவரம் </th>
            <th className="border-r border-gray-900 p-2 text-center">எண்ணிக்கை </th>
            <th className="border-r border-gray-900 p-2 text-center">மொத்த எடை (g)</th>
            <th className="border-r border-gray-900 p-2 text-center">நிகர எடை (g)</th>
            {/* <th className="p-2 text-center">மொத்த மதிப்பு </th> */}
          </tr>
        </thead>
        <tbody>
          {goldData.map((item, index) => (
            <tr key={index} className="border-b border-gray-900">
              <td className="border-r border-gray-900 p-2 capitalize">{item.goldType}</td>
              <td className="border-r border-gray-900 p-2 text-center">{item.quantity}</td>
              <td className="border-r border-gray-900 p-2 text-center">{item.grossWeight}</td>
              <td className="border-r border-gray-900 p-2 text-center">{item.netWeight}</td>
              {/* <td className="p-2 text-center">{formatCurrency(item.totalValue)}</td> */}
            </tr>
          ))}
          <tr className="border-t-2 border-gray-900 bg-gray-100 font-bold">
            <td className="border-r border-gray-900 p-2 text-right">மொத்தம் :</td>
            <td className="border-r border-gray-900 p-2 text-center">{calculateTotalCount()}</td>
            <td className="border-r border-gray-900 p-2 text-center">{calculateGrossWeightTotal().toFixed(2)}</td>
            <td className="border-r border-gray-900 p-2 text-center">{calculateNetWeightTotal().toFixed(2)}</td>
            {/* <td className="p-2 text-center">{formatCurrency(calculateTotal())}</td> */}
          </tr>
        </tbody>
      </table>
    </div>
  );
};

  // Reusable Signatures Component
const SignatureSection = () => (
  <div className="signature-section grid grid-cols-2 gap-12 mt-8">
    <div className="text-center">
      <div className="border-t-2 border-gray-900 pt-2 mt-10">
        <strong>கடன்தாரர் கையொப்பம் </strong>
      </div>
    </div>
    <div className="text-center">
      <div className="border-t-2 border-gray-900 pt-2 mt-10">
        <strong>மேலாளர் கையொப்பம்</strong>
      </div>
    </div>
  </div>
);


  // Updated Terms and Conditions Component with consistent styling
const TermsAndConditions = () => (
  <div className="terms-section mt-5 p-4 border border-gray-900 bg-gray-50">
    <div className="terms-title font-bold text-center mb-4 text-sm">
      விதிமுறைகள் மற்றும் நிபந்தனைகள்
    </div>
    <div className="text-xs leading-tight">
      <div className="flex items-start mb-2">
        <span className="font-bold mr-2 mt-0.5">•</span>
        <span>கடன் தொகைக்காக மாத மாதம் வட்டி செலுத்த வேண்டும்.</span>
      </div>
      <div className="flex items-start mb-2">
        <span className="font-bold mr-2 mt-0.5">•</span>
        <span>தொடர்ந்து மூன்று மாதங்கள் வட்டி செலுத்தப்படவில்லை என்றால், கடன் தொகைக்கு கூடுதல் அபராத வட்டி விதிக்கப்படும்.</span>
      </div>
      <div className="flex items-start mb-2">
        <span className="font-bold mr-2 mt-0.5">•</span>
        <span>நகைங்களை திரும்ப பெறும்போது இந்த ரசீதினை ஒப்படைக்க வேண்டும்.</span>
      </div>
      <div className="flex items-start mb-2">
        <span className="font-bold mr-2 mt-0.5">•</span>
        <span>தவணை தேதிக்கு பின் திரும்ப எடுக்காத நகைகள் ஏலத்திற்கு விடப்படும்.</span>
      </div>
      <div className="flex items-start mb-2">
        <span className="font-bold mr-2 mt-0.5">•</span>
        <span>ஏலத்தில் கிடைக்கும் தொகை கடன் தொகைக்கு போதுமானதாக இல்லாவிட்டால், மீதமுள்ள கடன் தொகையை முழுமையாக செலுத்தி கடன் கணக்கை நேர் செய்ய கடனுதாரரே பொறுப்பு.</span>
      </div>
      <div className="flex items-start">
        <span className="font-bold mr-2 mt-0.5">•</span>
        <span>தரப்பட்டுள்ள முகவரி விட்டு வேறு முகவரிக்கு மாறினால், கடன்தாரர் புதிய முகவரியை எழுத்து மூலம் தெரியப்படுத்த வேண்டும்.</span>
      </div>
    </div>
  </div>
);

  // Render company use section
  const renderCompanyUseSection = () => (
    <div ref={companyUseRef} className="receipt-section bg-white border-2 border-gray-900 p-6 font-mono text-sm">
      <div className="section-header text-center">COMPANY USE / நிறுவன பயன்பாட்டிற்கு</div>
      <br />
      <ReceiptHeader />
      <LoanDetails />
      <GoldItemsTable />
      <SignatureSection />
    </div>
  );

  // Render customer use section
const renderCustomerUseSection = () => (
  <div ref={customerUseRef} className="receipt-section bg-white border-2 border-gray-900 p-6 font-mono text-sm">
    <div className="section-header text-center">CUSTOMER USE / வாடிக்கையாளர் பயன்பாட்டிற்கு</div>
    <br />
    <ReceiptHeader />
    <LoanDetails />
    <GoldItemsTable />
    <TermsAndConditions />
    <SignatureSection />
    
    {/* Footer with optimized spacing - Only in customer section */}
    <div className="footer-section text-center mt-8 text-xs text-gray-600">
      <p>This is a computer generated receipt / இது கணினியால் உருவாக்கப்பட்ட ரசீது</p>
      <p>Generated on / உருவாக்கப்பட்ட தேதி: {new Date().toLocaleString('en-IN')}</p>
    </div>
  </div>
);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-900"></div>
        <span className="ml-3 text-gray-600">Loading loan receipt...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 mb-4">Error loading loan data: {error}</div>
        <button 
          onClick={fetchLoanData}
          className="bg-indigo-900 text-white px-4 py-2 rounded hover:bg-indigo-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Success Message */}
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Check size={32} className="text-green-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Loan Created Successfully!</h2>
        <p className="text-gray-600 mb-4">Your loan application has been submitted and processed.</p>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-4 mb-6 print:hidden justify-center">
        <button
          onClick={handleDownloadPDF}
          disabled={isGeneratingPDF}
          className={`flex items-center gap-2 px-4 py-2 rounded text-white ${
            isGeneratingPDF 
              ? 'bg-gray-400 cursor-not-allowed' 
              : 'bg-indigo-900 hover:bg-indigo-700'
          }`}
        >
          <Download size={16} />
          {isGeneratingPDF ? 'Generating PDF...' : 'Download PDF'}
        </button>
        <button
          onClick={handlePrint}
          className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
        >
          <Printer size={16} />
          Print Receipt
        </button>
      </div>

      <div ref={receiptRef}>
        {/* Company Use Section */}
        {renderCompanyUseSection()}
        
        {/* Dotted Line Separator */}
        <br />
        <br />
        <hr className="dotted-separator" />
        <br />
        <br /><br /><br /><br />

        {/* Customer Use Section */}
        {renderCustomerUseSection()}
      </div>
    </div>
  );
};
// Main Add New Loan Component

export default function AddNewLoanForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const { editLoan, loanId, loanNumber } = location.state || {};
  
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({});
  const [generatedId, setGeneratedId] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  
  // Add modal state and config here
  const [showModal, setShowModal] = useState(false);
  const [modalConfig, setModalConfig] = useState({ 
    title: '', 
    message: '', 
    type: 'success' 
  });
  
  const visibleSteps = ['Personal Details', 'Gold Details', 'Loan Info', 'Confirmation'];
  const allSteps = ['Personal Details', 'Gold Details', 'Loan Info', 'Photo Upload', 'Confirmation'];
  
  const getVisibleStepIndex = (step) => {
    const stepMap = {
      0: 0, // Personal Details
      1: 1, // Gold Details
      2: 2, // Loan Application
      3: 4, // Skip photo upload (index 3) and go to Confirmation (index 4)
      4: 4  // Confirmation
    };
    return stepMap[step];
  };

  const nextStep = () => {
    if (currentStep < allSteps.length - 1) {
      // Skip photo upload step (index 3) if we're moving from loan application (index 2)
      const nextStep = currentStep === 2 ? 4 : currentStep + 1;
      setCurrentStep(nextStep);
    }
  };

  const handleFinish = () => {
    navigate('/loans'); // Navigate to /loans route
  };

  const handleFinishEditing = () => {
    setShowConfirmation(true);
  };
  
  const prevStep = () => {
    if (currentStep > 0) {
      // Skip photo upload step (index 3) if we're moving back from confirmation (index 4)
      const prevStep = currentStep === 4 ? 2 : currentStep - 1;
      setCurrentStep(prevStep);
    }
  };
  
  // Edit mode: render all forms stacked
  const renderEditMode = () => {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <h2 className="text-2xl font-bold text-indigo-900 mb-4">Personal Details</h2>
          <PersonalDetailsForm 
            formData={formData} 
            setFormData={setFormData} 
            onNext={() => {}} // No navigation in edit mode
            editLoan={editLoan}
            loanId={loanId}
            setGeneratedId={setGeneratedId}
            generatedId={generatedId}
            setShowModal={setShowModal}
            setModalConfig={setModalConfig}
            hideNavigation={true} // Add prop to hide internal navigation
            hideTitle={true} // Add prop to hide form title
          />
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <h2 className="text-2xl font-bold text-indigo-900 mb-4">Gold Details</h2>
          <GoldDetailsForm 
            formData={formData} 
            setFormData={setFormData} 
            onNext={() => {}} // No navigation in edit mode
            onPrev={() => {}} // No navigation in edit mode
            editLoan={editLoan}
            loanId={loanId}
            generatedId={generatedId}
            setShowModal={setShowModal} 
            setModalConfig={setModalConfig}
            hideNavigation={true} // Add prop to hide internal navigation
            hideTitle={true} // Add prop to hide form title
          />
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <h2 className="text-2xl font-bold text-indigo-900 mb-4">Loan Info</h2>
          <LoanApplicationForm 
            formData={formData} 
            setFormData={setFormData} 
            onNext={() => {}} // No navigation in edit mode
            onPrev={() => {}} // No navigation in edit mode
            editLoan={editLoan} 
            loanId={loanId}  
            generatedId={generatedId} 
            setShowModal={setShowModal} 
            setModalConfig={setModalConfig}
            hideNavigation={true} // Add prop to hide internal navigation
            hideTitle={true} // Add prop to hide form title
          />
        </div>

        {/* Finish Editing Button */}
        <div className="flex justify-center mt-6">
          <button
            onClick={handleFinishEditing}
            className="px-8 py-3 mb-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
          >
            Receipt
          </button>
        </div>
      </div>
    );
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 0:
        return <PersonalDetailsForm 
          formData={formData} 
          setFormData={setFormData} 
          onNext={nextStep}
          editLoan={editLoan}
          loanId={loanId}
          setGeneratedId={setGeneratedId}
          generatedId={generatedId}
          setShowModal={setShowModal}
          setModalConfig={setModalConfig}
        />;
      case 1:
        return <GoldDetailsForm 
          formData={formData} 
          setFormData={setFormData} 
          onNext={nextStep}
          onPrev={prevStep}
          editLoan={editLoan}
          loanId={loanId}
          generatedId={generatedId}
          setShowModal={setShowModal} 
          setModalConfig={setModalConfig}
        />;
      case 2:
        return <LoanApplicationForm 
          formData={formData} 
          setFormData={setFormData} 
          onNext={nextStep}
          onPrev={prevStep}
          editLoan={editLoan} 
          loanId={loanId}  
          generatedId={generatedId} 
          setShowModal={setShowModal} 
          setModalConfig={setModalConfig}
        />;
      case 3:
        return <PhotoUploadForm 
          formData={formData} 
          setFormData={setFormData}
          onNext={nextStep}
          onPrev={prevStep}
          editLoan={editLoan}
          loanId={loanId}
          generatedId={generatedId}
        />;
      case 4:
        return <ConfirmationForm  
          editLoan={editLoan} 
          loanId={loanId}  
          generatedId={generatedId}
          onFinish={handleFinish}
        />;
      default:
        return <PersonalDetailsForm 
          formData={formData} 
          setFormData={setFormData} 
          onNext={nextStep}
          editLoan={editLoan}
          loanId={loanId}
          setGeneratedId={setGeneratedId}
          setShowModal={setShowModal}
          setModalConfig={setModalConfig}
        />;
    }
  };

  // Hide navigation buttons for Personal Details (0) and Gold Details (1) steps
  // Show navigation for Loan Application (2) to provide back button
  const showNavigationButtons = ![0, 1].includes(currentStep);

  // If in edit mode and showing confirmation
  if (editLoan && showConfirmation) {
    return (
      <div className="min-h-screen bg-gray-50">
         <div className=" mx-auto px-2 pt-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <ConfirmationForm  
              editLoan={editLoan} 
              loanId={loanId}  
              generatedId={generatedId}
              onFinish={handleFinish}
              formData={formData} // Pass updated form data
            />
          </div>
        </div>
        
        {/* Custom Modal */}
        <CustomSnackbar
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          title={modalConfig.title}
          message={modalConfig.message}
          type={modalConfig.type}
        />
      </div>
    );
  }

  // If in edit mode, show stacked forms
  if (editLoan && !showConfirmation) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-full mx-auto px-6 pt-6"> {/* Changed py-6 to pt-6 to remove top padding */}
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-900">
              Edit Loan - <span className="text-indigo-900">#{loanNumber}</span>
            </h1>
          </div>
          
          {renderEditMode()}
        </div>
        
        {/* Custom Modal */}
        <CustomSnackbar
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          title={modalConfig.title}
          message={modalConfig.message}
          type={modalConfig.type}
        />
      </div>
    );
  }

  // Normal step-by-step mode for new loans
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Show only visible steps in the progress bar for new loans */}
      <div className="max-w-full mx-auto px-6 overflow-hidden">
        <StepProgress 
          currentStep={getVisibleStepIndex(currentStep)} 
          steps={visibleSteps} 
        />
      </div>
      
      <div className="max-w-full mx-auto px-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          {renderCurrentStep()}
          
          {/* Navigation Buttons - Hide for Personal Details and Gold Details only */}
          {showNavigationButtons && (
            <div className="flex justify-between mt-6">
              {/* Show Back button for Loan Application (step 2) and Confirmation (step 4) */}
              {(currentStep === 2 || currentStep === 4) && (
                <button
                  onClick={prevStep}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Back
                </button>
              )}
              
              <div className="space-x-3">
                {currentStep === 4 && (
                  <button 
                    onClick={handleFinish} 
                    className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    Finish
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Custom Modal - Moved outside the navigation buttons div */}
      <CustomSnackbar
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={modalConfig.title}
        message={modalConfig.message}
        type={modalConfig.type}
      />
    </div>
  );
}